import { StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { getIntervalTimeOptions } from '../../../../models/UiUtils';
import { IInterval } from '../../../../viewmodels/AppViewModels';
import { baseStyleSheet } from '../../../styles/styles';
import { DefaultSelectBox, ISelectBoxOption } from '../../SelectBox';
import { styleSheet } from './styles';

interface IProps {
	canRemove?: boolean;
	className?: string;
	disabled?: boolean;
	interval?: IInterval;
	isFriday?: boolean;
	onUpdateInterval?(interval: IInterval): void;
	styles?: StyleDeclarationValue[];
}

// Default business hours: 8:00 AM to 6:00 PM
const DEFAULT_START_HOUR = 8;
const DEFAULT_END_HOUR = 18;
const MINUTES_PER_HOUR = 60;

export const defaultInterval: IInterval = {
	endMinutes: DEFAULT_END_HOUR * MINUTES_PER_HOUR,
	startMinutes: DEFAULT_START_HOUR * MINUTES_PER_HOUR,
};

export const IntervalDropdowns: React.FC<IProps> = ({
	canRemove,
	className,
	disabled,
	interval,
	isFriday,
	onUpdateInterval,
	styles,
}) => {
	const scheduleOptions = React.useMemo(() => getIntervalTimeOptions(), []);

	const onAddHours = React.useCallback(() => {
		onUpdateInterval?.(defaultInterval);
	}, [onUpdateInterval]);

	const onRemoveHours = React.useCallback(() => {
		onUpdateInterval?.(undefined);
	}, [onUpdateInterval]);

	const updateStartMinutes = React.useCallback(
		(selectedOption: ISelectBoxOption<number>) => {
			if (!interval) return;
			onUpdateInterval?.({
				endMinutes: interval.endMinutes,
				startMinutes: selectedOption.value,
			});
		},
		[interval, onUpdateInterval]
	);

	const updateEndMinutes = React.useCallback(
		(selectedOption: ISelectBoxOption<number>) => {
			if (!interval) return;
			onUpdateInterval?.({
				endMinutes: selectedOption.value,
				startMinutes: interval.startMinutes,
			});
		},
		[interval, onUpdateInterval]
	);

	if (!interval || (interval.startMinutes === 0 && interval.endMinutes === 0)) {
		return (
			<div className={css(styleSheet.container)} role='group' aria-label='Business hours settings'>
				<div className={css(styleSheet.promptText)}>
					{isFriday ? 'Same hours as Mon-Thur' : 'Outside business hours'}
				</div>
				<button
					className={css(baseStyleSheet.brandLink)}
					onClick={onAddHours}
					aria-label={isFriday ? 'Set different hours for Friday' : 'Add business hours for this day'}
					type='button'
				>
					{isFriday ? 'Change Hours' : 'Add Hours'}
				</button>
			</div>
		);
	}

	return (
		<div
			className={`${css(styleSheet.container, ...(styles || []))} interval-dropdowns ${className || ''}`}
			role='group'
			aria-label='Business hours time selection'
		>
			<DefaultSelectBox
				className={css(styleSheet.dropDown)}
				disabled={disabled}
				menuStyles={[styleSheet.dropDownOption]}
				onSelectionChanged={updateStartMinutes}
				options={scheduleOptions}
				selectedOption={scheduleOptions.find(x => x.value === interval?.startMinutes)}
				triggerStyles={[styleSheet.dropDownOption]}
			/>
			<div className={css(styleSheet.toText)} aria-hidden='true'>
				to
			</div>
			<DefaultSelectBox
				className={css(styleSheet.dropDown)}
				disabled={disabled}
				menuStyles={[styleSheet.dropDownOption]}
				onSelectionChanged={updateEndMinutes}
				options={scheduleOptions}
				selectedOption={scheduleOptions.find(x => x.value === interval?.endMinutes)}
				triggerStyles={[styleSheet.dropDownOption]}
			>
				{scheduleOptions?.map(x => (
					<option key={`end-${x.value}`} value={x.value}>
						{x.title as React.ReactNode}
					</option>
				))}
			</DefaultSelectBox>
			{!!canRemove && (
				<button
					className={css(baseStyleSheet.linkDestructive, styleSheet.removeButton)}
					onClick={onRemoveHours}
					aria-label='Remove business hours for this day'
					type='button'
				>
					Remove
				</button>
			)}
		</div>
	);
};
